import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import type {
  TaskEditorBranchScope,
  TaskEditorState,
} from "./utils/taskEditorUtils";
import {
  buildTaskEditorBlockGroups,
  getTaskEditorBlockDisplayLabels,
  getTaskEditorBranchSegmentBlocks,
} from "./utils/taskEditorUtils";
import TaskEditorLivePreview from "./TaskEditorLivePreview";
import TaskEditorWorkspace from "./TaskEditorWorkspace";
import TaskEditorCodeView from "./TaskEditorCodeView";
import { taskEditorScrollbarSx } from "./utils/taskEditorScrollbarSx";
import CustomModal from "components/common/CustomModal";
import UndoIcon from "@mui/icons-material/Undo";

interface TaskWorkspacePanelProps {
  editorState?: TaskEditorState;
  onChangeEditorState: (next: TaskEditorState) => void;
  hasUnsavedChanges: boolean;
  isCommitting: boolean;
  onCommit: () => void;
  onEditMeta: () => void;
  // hasUnsavedChanges=true 일 때만 의미. working vs V1 비교·복원에 사용.
  v1Task?: TaskEditorState;
  onDiscardWorking?: () => void | Promise<void>;
  // true 면 본문을 hydration skeleton 으로 대체 — 첫 로드/전환 시 detail 미로드 구간.
  // 트리거 판정(detailLoadedTaskIds)은 부모(TaskEditorPage)가 렌더 중 파생한다.
  isHydrating?: boolean;
  // true 면 활동지가 0개 — "활동지를 선택하거나 만드세요" 빈 안내. 로딩 중엔 false(→ skeleton).
  showNoTasks?: boolean;
}

type WorkspaceTab = "edit" | "preview" | "code";

const TAB_LABELS: Record<WorkspaceTab, string> = {
  edit: "편집",
  preview: "미리보기",
  code: "코드",
};

const KEY_BADGE_COLOR = "#F59E0B";

// 활동지 전환 직후 잠깐 보이는 헤더/본문/푸터 블록 자리를 채울 hydration skeleton 카드.
// MyProgressV2(진도표) 로딩 카드와 동일한 스타일 — 둥근 16px, Surface.Forward, 테두리/그림자 없음.
const hydrationSkeletonCardSx = {
  borderRadius: "16px",
  px: "20px",
  py: "18px",
  backgroundColor: theme.vars.palette.Surface.Forward,
} as const;

// 첫 로드/전환 시 detail 미도착 구간 — 실제로 잠깐 보이던 헤더/본문/푸터 블록 자리를 같은
// 레이아웃의 skeleton 으로 대체(MyProgressV2 진도표 로딩 스타일). 초기 로드와 본문 로딩 두 곳에서 재사용.
function HydrationSkeleton() {
  return (
    <Box
      data-testid="task-editor-hydration-skeleton"
      sx={{ px: "12px", py: "16px" }}
    >
      <Stack spacing={1} sx={{ maxWidth: "660px", mx: "auto", width: "100%" }}>
        {/* 헤더 블록 */}
        <Box sx={hydrationSkeletonCardSx}>
          <Skeleton
            variant="rectangular"
            height={28}
            sx={{ borderRadius: "8px", width: "45%" }}
          />
        </Box>
        {/* 본문 블록 */}
        <Box sx={hydrationSkeletonCardSx}>
          <Stack spacing={1}>
            <Skeleton
              variant="rectangular"
              height={28}
              sx={{ borderRadius: "8px" }}
            />
            <Skeleton
              variant="rectangular"
              height={28}
              sx={{ borderRadius: "8px", width: "80%" }}
            />
          </Stack>
        </Box>
        {/* 푸터(마치기) 블록 — 흐림 */}
        <Box sx={{ ...hydrationSkeletonCardSx, opacity: 0.55 }}>
          <Skeleton
            variant="rectangular"
            height={28}
            sx={{ borderRadius: "8px", width: "35%" }}
          />
        </Box>
      </Stack>
    </Box>
  );
}

export default function TaskWorkspacePanel({
  editorState,
  onChangeEditorState,
  hasUnsavedChanges,
  isCommitting,
  onCommit,
  onEditMeta,
  v1Task,
  onDiscardWorking,
  isHydrating,
  showNoTasks,
}: TaskWorkspacePanelProps) {
  const [tab, setTab] = useState<WorkspaceTab>("edit");
  // 미리보기 탭을 한 번 열면 이후엔 display 토글로만 숨겨 마운트를 유지한다
  // — 체험 모드 토글/진행(usePreviewSession 상태)이 탭 전환에도 보존되게.
  const [hasPreviewMounted, setHasPreviewMounted] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  // 미리보기에서 블록을 눌러 편집 탭으로 넘어갈 때, 포커스할 블록 index(0-based,
  // 현재 스코프 기준). 편집 워크스페이스가 마운트되며 이 값을 소비하고
  // onFocusConsumed 로 비운다.
  const [pendingFocusBlockIndex, setPendingFocusBlockIndex] = useState<
    number | null
  >(null);
  // 분기 드릴다운 스코프 — 워크스페이스(편집)와 미리보기가 공유. 워크스페이스는
  // 편집 탭을 벗어나면 언마운트되므로 패널이 소유해 탭 전환에도 유지한다.
  const [branchScope, setBranchScope] = useState<TaskEditorBranchScope | null>(
    null
  );

  // 활동지 전환 시 스코프 해제.
  const taskId = editorState?.task_id;
  useEffect(() => {
    setBranchScope(null);
  }, [taskId]);

  const blockGroups = useMemo(
    () => buildTaskEditorBlockGroups(editorState?.blocks ?? []),
    [editorState]
  );

  // 하이러키 바의 "블록 N" 라벨용 — 드릴다운 브레드크럼에서 사용.
  const displayLabels = useMemo(
    () => getTaskEditorBlockDisplayLabels(editorState?.blocks ?? []),
    [editorState]
  );

  // 분기 내(세그먼트) 미리보기 — 드릴다운 중에는 그 옵션의 내부 블록만 보여준다.
  const previewTask = useMemo(() => {
    if (!editorState || !branchScope) return editorState;
    return {
      ...editorState,
      blocks: getTaskEditorBranchSegmentBlocks(
        editorState.blocks,
        branchScope.blockIndex,
        branchScope.optionIndex
      ),
    };
  }, [editorState, branchScope]);

  const handleEditBlock = useCallback(
    (blockIndex: number) => {
      setTab("edit");
      // 스코프 미리보기의 ✎ — index 가 이미 세그먼트 로컬이라 그대로 포커스.
      if (branchScope) {
        setPendingFocusBlockIndex(blockIndex);
        return;
      }
      // 루트 미리보기의 ✎ — 세그먼트 블록이면 그 옵션 드릴다운으로 진입해
      // 해당 블록(로컬 index)을 포커스, 메인 블록이면 루트에서 포커스.
      for (const group of blockGroups) {
        if (group.kind !== "branch") continue;
        for (const segment of group.segments) {
          if (blockIndex >= segment.start && blockIndex < segment.end) {
            setBranchScope({
              blockIndex: group.index,
              optionIndex: segment.optionIndex,
            });
            setPendingFocusBlockIndex(blockIndex - segment.start);
            return;
          }
        }
      }
      setPendingFocusBlockIndex(blockIndex);
    },
    [branchScope, blockGroups]
  );

  const handleFocusConsumed = useCallback(() => {
    setPendingFocusBlockIndex(null);
  }, []);

  // 활동지가 정말 0개일 때만 빈 안내. (로딩 중엔 showNoTasks=false → 아래 skeleton)
  if (showNoTasks) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: theme.vars.palette.Content.Weak,
          p: "32px",
        }}
      >
        <Typography level="body-md" sx={{ mb: "4px" }}>
          활동지를 선택하거나 만드세요
        </Typography>
        <Typography
          level="body-xs"
          sx={{ color: theme.vars.palette.Content.Weak }}
        >
          좌측 목록에서 활동지를 선택하거나 [+ 새 활동지] 버튼을 눌러
          시작하세요.
        </Typography>
      </Box>
    );
  }
  // 초기 로드(메타 도착 전, editorState 없음) — 헤더 없이 본문 skeleton 만. 빈 안내가 새지 않는다.
  // (editorState 가 있으면 헤더/제목은 유지하고 아래 본문에서만 skeleton 으로 전환한다.)
  if (!editorState) {
    return <HydrationSkeleton />;
  }

  const isEmptyType = editorState.task_type === "empty";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        backgroundColor: theme.vars.palette.Background.Level1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: "12px",
          py: "8px",
          borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
          backgroundColor: theme.vars.palette.Surface.Forward,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ minWidth: 0, flexWrap: "nowrap" }}
          >
            <Typography
              level="title-sm"
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                color: theme.vars.palette.Content.Strong,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {editorState.title || "(제목 없음)"}
            </Typography>
            {editorState.is_key_activity && (
              <Typography
                level="body-xs"
                sx={{
                  color: KEY_BADGE_COLOR,
                  fontSize: "12px",
                  flexShrink: 0,
                }}
                title="Key 활동지"
              >
                ★
              </Typography>
            )}
            <Typography
              level="body-xs"
              sx={{
                color: theme.vars.palette.Content.Medium,
                fontSize: "11px",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {editorState.task_id || "(ID 없음)"} ·{" "}
            </Typography>
            {editorState.set_number !== undefined && (
              <Typography
                level="body-xs"
                sx={{
                  color: theme.vars.palette.Content.Weak,
                  fontSize: "11px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                세트 {editorState.set_number}
              </Typography>
            )}
            <Box
              className="meta-edit-btn"
              onClick={onEditMeta}
              sx={{
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                lineHeight: 0,
                color: theme.vars.palette.Content.Weak,
                cursor: "pointer",
                px: "2px",
                "&:hover": {
                  color: theme.vars.palette.Content.Strong,
                },
              }}
              title="메타데이터 편집"
            >
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: "16px",
                }}
              >
                more_vert
              </span>
            </Box>
          </Stack>
        </Box>

        {hasUnsavedChanges && (
          <>
            <Box
              component="button"
              type="button"
              onClick={() => setDiscardConfirmOpen(true)}
              disabled={!v1Task || isCommitting || !onDiscardWorking}
              aria-label="원복"
              title="원복"
              sx={{
                background: "none",
                border: "none",
                padding: 0,
                margin: 0,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                lineHeight: 0,
                mr: "8px",
                color: theme.vars.palette.Content.Weak,
                "&:hover": { color: theme.vars.palette.Content.Medium },
                "&:disabled": {
                  color: theme.vars.palette.Content.Weak,
                  opacity: 0.4,
                  cursor: "default",
                },
              }}
            >
              <UndoIcon sx={{ fontSize: "18px" }} />
            </Box>
          </>
        )}
        <Button
          size="sm"
          variant="Solid"
          color="primary"
          onClick={onCommit}
          disabled={!hasUnsavedChanges || isCommitting}
          loading={isCommitting}
          sx={{ fontSize: "12px", minHeight: "28px" }}
        >
          {"커밋"}
        </Button>
      </Box>

      <CustomModal
        open={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        title="변경 내용을 원복할까요?"
        subtitle="커밋되지 않은 변경 내용이 모두 사라지고 마지막 커밋 상태로 되돌아갑니다."
        actions={[
          {
            label: "원복하기",
            variant: "Solid",
            color: "danger",
            onClick: () => {
              setDiscardConfirmOpen(false);
              void onDiscardWorking?.();
            },
          },
          {
            label: "취소",
            variant: "Plain",
            onClick: () => setDiscardConfirmOpen(false),
          },
        ]}
      />

      {/* 깡통 활동지 — 내부 content 가 없어 편집/미리보기/코드 모두 대상이 아니다.
          탭 바와 콘텐츠 영역 전체를 안내로 대체한다. */}
      {isEmptyType ? (
        <Box
          data-testid="task-editor-empty-type-notice"
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: theme.vars.palette.Content.Weak,
            p: "32px",
          }}
        >
          <Typography level="body-md" sx={{ mb: "4px" }}>
            내용이 없는 활동지입니다
          </Typography>
          <Typography
            level="body-xs"
            sx={{ color: theme.vars.palette.Content.Weak }}
          >
            편집하거나 미리볼 내용이 없어요.
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              gap: "4px",
              px: "12px",
              pt: "6px",
              borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
              backgroundColor: theme.vars.palette.Surface.Forward,
            }}
          >
            {(["edit", "preview", "code"] as const).map((tabId) => {
              const isActive = tab === tabId;
              return (
                <Box
                  key={tabId}
                  onClick={() => {
                    setTab(tabId);
                    if (tabId === "preview") setHasPreviewMounted(true);
                  }}
                  sx={{
                    px: "10px",
                    py: "6px",
                    cursor: "pointer",
                    borderBottom: `2px solid ${
                      isActive
                        ? theme.vars.palette.Content.Strong
                        : "transparent"
                    }`,
                    color: isActive
                      ? theme.vars.palette.Content.Strong
                      : theme.vars.palette.Content.Weak,
                    fontWeight: isActive ? 700 : 500,
                    fontSize: "12px",
                    userSelect: "none",
                  }}
                >
                  {TAB_LABELS[tabId]}
                </Box>
              );
            })}
          </Box>

          {/* 탭 바 아래 고정 하이러키 바 — 분기 드릴다운 중에만 노출.
              스크롤 영역 밖이라 블록을 스크롤해도 그대로 남는다. 스코프는
              편집·미리보기 탭이 공유하므로 탭과 무관하게 표시. */}
          {branchScope && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                height: "38px",
                // 탭 글자 시작선(컨테이너 12px + 탭 px 10px)과 왼쪽 라인 맞춤.
                pl: "22px",
                pr: "12px",
                flexShrink: 0,
                minWidth: 0,
                borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
                backgroundColor: theme.vars.palette.Surface.Forward,
              }}
            >
              {(() => {
                const branchBlock = editorState.blocks[branchScope.blockIndex];
                const optionText =
                  branchBlock?.action?.type === "button_branch"
                    ? (branchBlock.action.options[branchScope.optionIndex]
                        ?.text ?? "")
                    : "";
                const branchLabel =
                  displayLabels[branchScope.blockIndex] ??
                  `블록 ${branchScope.blockIndex + 1}`;
                // 탭 라벨(12px)보다 커 보이지 않게 12px 로 통일.
                const separatorSx = {
                  flexShrink: 0,
                  fontSize: "12px",
                  color: theme.vars.palette.Content.SuperWeak,
                  userSelect: "none" as const,
                };
                return (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    data-testid="task-editor-branch-scope-breadcrumb"
                    sx={{ minWidth: 0 }}
                  >
                    {/* Joy Button 은 테마 hover 가 끼어들고 밑줄이 아이콘까지
                        그어져 어색 — 순수 텍스트 버튼으로 만들고 hover 밑줄은
                        글자에만 건다. */}
                    <Box
                      component="button"
                      type="button"
                      data-testid="task-editor-branch-scope-back"
                      onClick={() => setBranchScope(null)}
                      sx={{
                        p: 0,
                        border: "none",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontSize: "12px",
                        fontWeight: 600,
                        flexShrink: 0,
                        color: theme.vars.palette.Content.Medium,
                        "&:hover": {
                          color: theme.vars.palette.Content.Strong,
                          textDecoration: "underline",
                          textUnderlineOffset: "3px",
                        },
                      }}
                    >
                      전체
                    </Box>
                    <Typography component="span" sx={separatorSx}>
                      ›
                    </Typography>
                    {/* 블록 N — 루트로 나가며 그 분기 블록을 하이라이트 +
                        스크롤 (✎ 포커스 메커니즘 재사용). */}
                    <Box
                      component="button"
                      type="button"
                      data-testid="task-editor-branch-scope-block"
                      onClick={() => {
                        setTab("edit");
                        setPendingFocusBlockIndex(branchScope.blockIndex);
                        setBranchScope(null);
                      }}
                      sx={{
                        p: 0,
                        border: "none",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontSize: "12px",
                        fontWeight: 600,
                        flexShrink: 0,
                        color: theme.vars.palette.Content.Medium,
                        "&:hover": {
                          color: theme.vars.palette.Content.Strong,
                          textDecoration: "underline",
                          textUnderlineOffset: "3px",
                        },
                      }}
                    >
                      {branchLabel}
                    </Box>
                    <Typography component="span" sx={separatorSx}>
                      ›
                    </Typography>
                    <Typography
                      noWrap
                      sx={{
                        minWidth: 0,
                        fontSize: "12px",
                        fontWeight: 700,
                        color: theme.vars.palette.Content.Strong,
                      }}
                    >
                      {`옵션 ${branchScope.optionIndex + 1}${
                        optionText ? ` · ${optionText}` : ""
                      }`}
                    </Typography>
                  </Stack>
                );
              })()}
            </Stack>
          )}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              // 숨김 미리보기 오버레이(absolute inset:0)의 기준 박스
              position: "relative",
              ...taskEditorScrollbarSx,
            }}
          >
            {/* skeleton 은 편집 탭에서만. 미리보기/코드 탭은 hydrating 중 stale 본문을 그리지 않도록
                (가짜 diff/미리보기 깜빡임 방지) 준비 전엔 아무것도 렌더하지 않는다. */}
            {tab === "edit" &&
              (isHydrating ? (
                <HydrationSkeleton />
              ) : (
                <TaskEditorWorkspace
                  editorState={editorState}
                  onChangeEditorState={onChangeEditorState}
                  branchScope={branchScope}
                  onBranchScopeChange={setBranchScope}
                  focusBlockIndex={pendingFocusBlockIndex}
                  onFocusConsumed={handleFocusConsumed}
                />
              ))}
            {hasPreviewMounted && !isHydrating && (
              <Box
                sx={{
                  px: "12px",
                  py: "8px",
                  // display:none 은 레이아웃을 제거해 내부 스크롤 위치까지 날린다.
                  // absolute 오버레이(패널 크기 고정) + visibility 토글로 지오메트리를
                  // 유지해, 숨겼다 돌아와도 보던 스크롤 위치가 그대로 보존되게 한다.
                  // 높이가 고정되면서 LivePreview 내부 스크롤러(flex:1 overflow:auto)가
                  // 편집/코드 탭(height:100%)과 동일하게 동작한다.
                  position: "absolute",
                  inset: 0,
                  visibility: tab === "preview" ? "visible" : "hidden",
                }}
              >
                <TaskEditorLivePreview
                  task={previewTask}
                  onEditBlock={handleEditBlock}
                  isActive={tab === "preview"}
                  showFooterBlocks={!branchScope}
                />
              </Box>
            )}
            {tab === "code" && !isHydrating && (
              <Box
                sx={{
                  px: "9%",
                  pt: "8px",
                  pb: "12px",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                <TaskEditorCodeView task={editorState} v1Task={v1Task} />
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
